import { HttpClient } from "@angular/common/http";
import { TranslateLoader } from "@ngx-translate/core";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { environment } from "@mypxplat/xplat/core";

/**
 * Custom TranslateLoader that injects environment variables into translation strings.
 * This allows you to use {{brandName}}, {{appName}}, etc. in your translation files
 * without passing them every time you use the translate pipe.
 */
export class EnvironmentTranslateLoader implements TranslateLoader {
  constructor(
    private http: HttpClient,
    private prefix: string = "./assets/i18n/",
    private suffix: string = ".json"
  ) {}

  /**
   * Gets the translations from the server and processes them to replace
   * environment variable placeholders with actual values
   */
  getTranslation(lang: string): Observable<any> {
    return this.http.get(`${this.prefix}${lang}${this.suffix}`).pipe(
      map((translations: any) => {
        return this.processTranslations(translations);
      })
    );
  }

  /**
   * Recursively processes translation object and replaces environment variable
   * placeholders with actual values from the environment
   */
  private processTranslations(obj: any): any {
    if (typeof obj === "string") {
      return this.replaceEnvironmentVars(obj);
    }

    if (Array.isArray(obj)) {
      return obj.map((item) => this.processTranslations(item));
    }

    if (typeof obj === "object" && obj !== null) {
      const processed: any = {};
      for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
          processed[key] = this.processTranslations(obj[key]);
        }
      }
      return processed;
    }

    return obj;
  }

  /**
   * Replaces environment variable placeholders in a string
   * Supports: {{ENV:variableName}} syntax
   * Example: "Subscribe to {{ENV:studioOnePlusBrandName}}"
   */
  private replaceEnvironmentVars(str: string): string {
    return str.replace(/\{\{ENV:([^}]+)\}\}/g, (match, varName) => {
      // Navigate through nested environment properties
      const value = this.getNestedProperty(environment, varName);
      return value !== undefined ? value : match;
    });
  }

  /**
   * Gets a nested property from an object using dot notation
   * Example: 'studioOnePlusBrandName' or 'features.cookie_management'
   */
  private getNestedProperty(obj: any, path: string): any {
    return path.split(".").reduce((current, prop) => current?.[prop], obj);
  }
}
